import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  IconButton,
  useToast,
  Spinner,
  Center
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { fetchWithAuth } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'
import AddAccountsModal from './AddAccountsModal'
import AddSearchTermsModal from './AddSearchTermsModal'
import DeleteConfirmModal from './DeleteConfirmModal'

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showAddSearchModal, setShowAddSearchModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const toast = useToast()
  const navigate = useNavigate()
  const { API_BASE, user, logout } = useAuth()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  useEffect(() => {
    fetchAccounts()
  }, [])

  const fetchAccounts = async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await fetchWithAuth(`${API_BASE}/api/accounts`)
      const data = await resp.json()
      // API returns string array ["ANI", "PTI"], map to objects for display
      const accountsArray = data.accounts || []
      setAccounts(accountsArray)
    } catch (e) {
      setError(`Failed to load accounts: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (handle) => {
    // Filter out the deleted handle from string array
    const updated = accounts.filter(h => h !== handle)
    try {
      const resp = await fetchWithAuth(`${API_BASE}/api/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accounts: updated })
      })
      setAccounts(updated)
      setDeleteConfirm(null)
      toast({
        title: 'Account deleted',
        description: `@${handle} has been removed`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (e) {
      setError(`Failed to delete account: ${e.message}`)
      toast({
        title: 'Error',
        description: e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleAddAccounts = async (newHandles) => {
    // Combine as string arrays
    const combined = [...accounts, ...newHandles]
    try {
      const resp = await fetchWithAuth(`${API_BASE}/api/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accounts: combined })
      })
      await fetchAccounts()
      setShowAddModal(false)
      toast({
        title: 'Accounts added',
        description: `Added ${newHandles.length} account${newHandles.length !==1 ? 's' : ''}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (e) {
      throw e // propagate to modal
    }
  }

  const count = accounts.length
  const max = 80

  if (loading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text color="gray.400">Loading accounts...</Text>
        </VStack>
      </Center>
    )
  }

  if (error) {
    return (
      <Center h="50vh">
        <Text color="red.400" fontSize="lg">{error}</Text>
      </Center>
    )
  }

  return (
    <Box maxW="800px" mx="auto" px={2} minH="80vh">
      <Flex justify="space-between" align="center" mb={3}>
        <IconButton
          icon={<span>â†</span>}
          onClick={() => navigate('/app')}
          aria-label="Back to Feed"
          title="Back to Feed"
          variant="ghost"
          colorScheme="brand"
          size="md"
          fontSize="20px"
          _hover={{ bg: 'whiteAlpha.100' }}
        />
        
        {user && (
          <Text
            fontSize="md"
            color="gray.400"
            fontWeight="500"
          >
            {user.username}
          </Text>
        )}
        
        <IconButton
          icon={<span>â†ª</span>}
          onClick={handleLogout}
          aria-label="Logout"
          title="Logout"
          variant="ghost"
          colorScheme="red"
          size="md"
          fontSize="20px"
          _hover={{ bg: 'whiteAlpha.100' }}
        />
      </Flex>

      <VStack spacing={1} align="stretch" mb={3}>
        <Heading size="lg" color="#fafafa">Account Management</Heading>
        <Text 
          fontSize="md" 
          fontWeight="500"
          color={count >= max ? 'red.400' : 'brand.500'}
        >
          {count} / {max} accounts
        </Text>
      </VStack>

      <HStack spacing={2} mb={3} mt={2}>
        <Button
          bgGradient="linear(to-br, #c2410c, #ea580c)"
          color="#fafafa"
          border="1px solid"
          borderColor="#9a3412"
          _hover={{
            borderColor: '#fb923c',
            transform: 'translateY(-1px)',
            shadow: 'lg',
          }}
          onClick={() => setShowAddModal(true)}
          isDisabled={count >= max}
          size="md"
          flex={1}
        >
          + Add Accounts
        </Button>

        <Button
          bgGradient="linear(to-br, #c2410c, #ea580c)"
          color="#fafafa"
          border="1px solid"
          borderColor="#9a3412"
          _hover={{
            borderColor: '#fb923c',
            transform: 'translateY(-1px)',
            shadow: 'lg',
          }}
          onClick={() => setShowAddSearchModal(true)}
          isDisabled={count >= max}
          size="md"
          flex={1}
        >
          + Add Search Terms
        </Button>
      </HStack>

      <VStack spacing={1} align="stretch">
        {accounts.length === 0 ? (
          <Center py={10}>
            <Text color="gray.400" fontSize="md">
              No accounts configured. Add some to start monitoring!
            </Text>
          </Center>
        ) : (
          accounts.map(item => {
            const isAccount = item.startsWith('@')
            const displayText = item
            const typeColor = isAccount ? '#f97316' : '#60a5fa'  // Orange for accounts, blue for search terms
            const typeLabel = isAccount ? 'Account' : 'Search Term'

            return (
              <Flex
                key={item}
                bg="#131316"
                border="1px solid"
                borderColor="#27272a"
                borderRadius="10px"
                py={2}
                px={3}
                justify="space-between"
                align="center"
                transition="all 150ms ease"
                _hover={{
                  borderColor: '#3f3f46',
                  shadow: 'md',
                }}
              >
                <VStack align="start" spacing={0}>
                  <Text fontSize="md" color={typeColor} fontWeight="500">
                    {displayText}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {typeLabel}
                  </Text>
                </VStack>
                <IconButton
                  icon={<span>âœ•</span>}
                  onClick={() => setDeleteConfirm(item)}
                  aria-label={`Delete ${item}`}
                  size="sm"
                  variant="ghost"
                  color="red.400"
                  _hover={{
                    bg: 'red.500',
                    color: 'white',
                  }}
                />
              </Flex>
            )
          })
        )}
      </VStack>

      {deleteConfirm && (
        <DeleteConfirmModal
          handle={deleteConfirm}
          onConfirm={() => handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {showAddModal && (
        <AddAccountsModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddAccounts}
          currentCount={count}
          maxCount={max}
        />
      )}

      {showAddSearchModal && (
        <AddSearchTermsModal
          onClose={() => setShowAddSearchModal(false)}
          onAdd={handleAddAccounts}
          currentCount={count}
          maxCount={max}
        />
      )}
    </Box>
  )
}
